import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ReactComponent as Save } from 'assets/icons/save.svg';
import { flatten } from 'flat';
import {
	getLockedFields,
	SERVICE_DETAILS_FEE_TITLES,
	SERVICE_DETAILS_PAYMENT_METHODS_TITLES,
	SERVICE_DETAILS_REG_TITLES,
	SERVICE_DETAILS_SUM_TITLES,
	SERVICE_DETAILS_TITLES,
} from 'pages/ServiceDetailsPage/config';
import { getServiceDetailSelector, updateServiceFee } from 'store/bill-services';
import { getServiceById, updateServiceById } from 'store/bill-services/actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
	IContractFee,
	IFeeObjectAllData,
	IGetServiceDetailsResponse,
	IPaymentMethodsSelectData,
	IRegDataObject,
	IUpdateServiceFeeRequest,
	IUpdateServiceRequest,
} from 'types';
import toastService from 'components/elements/Toastify';
import { DetailsContent } from 'components/modules/DetailsContent';
import { DetailsHeader } from 'components/modules/DetailsHeader';
import Loading from 'components/modules/Loading';
import { SERVICES_LOCKED_FEE_FIELDS, SERVICES_LOCKED_REG_FIELDS } from 'utils/constants';
import {
	SERVICE_DETAILS_FEE_KEYS,
	SERVICE_DETAILS_KEYS,
	SERVICE_DETAILS_PAYMENT_METHODS_KEYS,
	SERVICE_DETAILS_REG_KEYS,
} from 'utils/enums';
import { fixedAmount, formatPaymentMehodsFromSelect, generatePaymentMethodsSelectOptions } from 'utils/helpers';
import { removePrefix } from 'utils/helpers/remove-prefix';
import { usePermissions } from 'utils/hooks/use-permissions';
import s from './index.module.scss';

// TODO remove commented block when will be approve

export const ServiceDetailsPage: FC = () => {
    const dispatch = useAppDispatch();
    const { id } = useParams();
    const canI = usePermissions();

    const [serviceDetailsData, setServiceDetailsData] = useState<null | IGetServiceDetailsResponse>(null);
    const [serviceDetailsRegData, setServiceDetailsRegData] = useState<IRegDataObject>();
    const [serviceDetailFeeInnerData, setServiceDetailFeeInnerData] = useState<any>(); // TODO CHANGE TYPE AND OPTIMIZE CODE
    const [serviceDetailFeeOuterData, setServiceDetailFeeOuterData] = useState<any>();
    const [serviceUpdatedData, setServiceUpdatedData] = useState({});
    const [serviceUpdatedFeeInnerData, setServiceUpdatedFeeInnerData] = useState<IFeeObjectAllData | null>(null);
    const [serviceUpdatedFeeOuterData, setServiceUpdatedFeeOuterData] = useState<IFeeObjectAllData | null>(null);
    const [serviceDetailsPaymentMethods, setServiceDetailsPaymentMethods] = useState<IPaymentMethodsSelectData | null>(
		null
	);
	const [serviceUpdatedPaymentMethods, setServiceUpdatedPaymentMethods] = useState<IPaymentMethodsSelectData | null>(
		null
	);

    const serviceDetails = useAppSelector(getServiceDetailSelector);

    useEffect(() => {
        if (id) {
            dispatch(getServiceById(id));
        }
    }, [id]);

    useEffect(() => {
        if (serviceDetails) {
            setServiceDetailsData(serviceDetails);
            setServiceDetailsRegData(serviceDetails.regDataObject);
			setServiceDetailsPaymentMethods(serviceDetails.paymentMethods ? generatePaymentMethodsSelectOptions(serviceDetails.paymentMethods) : null);
            serviceDetails.servicesContractsMap[0].contractFee.map((feeItem: IContractFee) => {
                const feeAllData = removePrefix(removePrefix(flatten(feeItem), 'feeObject.'), 'fee.');
                feeAllData.feeMax = fixedAmount(feeAllData.feeMax)
                feeAllData.feeMin = fixedAmount(feeAllData.feeMin)
                feeAllData.fixedAmount = fixedAmount(feeAllData.fixedAmount)
                feeAllData.sumFrom = fixedAmount(feeAllData.sumFrom)
                feeAllData.sumTo = fixedAmount(feeAllData.sumTo)
                feeItem.feeDirectionType === 'INNER'
                    ? setServiceDetailFeeInnerData(feeAllData)
                    : setServiceDetailFeeOuterData(feeAllData)
            });

        }
    }, [serviceDetails]);

    const handleUpdateServiceData = (data: IUpdateServiceRequest) => {
        setServiceUpdatedData({ ...serviceUpdatedData, ...data });
    };

    const handleUpdateServiceFeeInnerData = (data: Partial<IFeeObjectAllData>) => {
        setServiceUpdatedFeeInnerData((prevState) => ({
            ...(prevState || {}),
            ...data
        } as IFeeObjectAllData));
    };

    const handleUpdateServiceFeeOuterData = (data: Partial<IFeeObjectAllData>) => {
        setServiceUpdatedFeeOuterData((prevState) => ({
            ...(prevState || {}),
            ...data
        } as IFeeObjectAllData));
    };

    const handleUpdateServicePaymentMethods = (data: Partial<IPaymentMethodsSelectData>) => {
		setServiceUpdatedPaymentMethods((prev) => ({ ...(prev || {}), ...data } as IPaymentMethodsSelectData));
	};

	const handleSubmitUpdateService = () => {
		dispatch(updateServiceById({ serviceId: id, ...serviceUpdatedData }));
	};

	const handleSubmitUpdateServicePaymentMethods = async () => {
        if(!serviceUpdatedPaymentMethods) return;
    
        try {
            const updatedPaymentMethods = { ...serviceDetailsPaymentMethods, ...serviceUpdatedPaymentMethods };
            // if(Object.values(updatedPaymentMethods).every((value) => value === TOGGLE_VARIANTS.FALSE)) {
            //     toastService.error('Хоча б 1 метод оплати повинен бути доступним');
            //     return;
            // }
			await dispatch(
				updateServiceById({
					serviceId: id,
					paymentMethods: formatPaymentMehodsFromSelect(updatedPaymentMethods)
				})
			).unwrap();
            setServiceUpdatedPaymentMethods(null);
            toastService.success('Методи для оплати сервісу було успішно змінено')
        } catch (error) {
			toastService.error('При зміні методів оплати сервісу щось пішло не так');
        }	
	};

    const handleSubmitUpdateFeeService = (TypeFee: string) => {
        let serviceFeeUpdatedData: IFeeObjectAllData | null = null;

        if (TypeFee === 'INNER') {
            serviceFeeUpdatedData = { ...serviceDetailFeeInnerData, ...serviceUpdatedFeeInnerData };
            setServiceUpdatedFeeInnerData(null)
        } else {
            serviceFeeUpdatedData = { ...serviceDetailFeeOuterData, ...serviceUpdatedFeeOuterData };
            setServiceUpdatedFeeOuterData(null)
        }
        if (serviceFeeUpdatedData) {
            const reqBody: IUpdateServiceFeeRequest = {
                serviceId: serviceFeeUpdatedData.serviceId,
                feeDirectionType: serviceFeeUpdatedData.feeDirectionType,
                feeObject: {
                    sumFrom: fixedAmount(serviceFeeUpdatedData.sumFrom),
                    sumTo: fixedAmount(serviceFeeUpdatedData.sumTo),
                    feeMin: fixedAmount(serviceFeeUpdatedData.feeMin),
                    feeMax: fixedAmount(serviceFeeUpdatedData.feeMax),
                    fee: {
                        fixedAmount: Number(fixedAmount(serviceFeeUpdatedData.fixedAmount)),
                        percent: serviceFeeUpdatedData.percent,
                    }
                },
                outerFeeForbidden: serviceFeeUpdatedData.outerFeeForbidden,
                contractFeeId: serviceFeeUpdatedData.id,
            };
            dispatch(updateServiceFee(reqBody));

        } else {
            console.error('Service fee data is not available');
        }
    };

    if (!serviceDetailsData) return <Loading/>;

    const isClientFeeForbidden = serviceDetailsData.servicesContractsMap?.[0]?.contractFee?.[0]?.outerFeeForbidden === true;

    return (
        <Box className={s.wrapper}>
            <DetailsHeader title={serviceDetailsData?.name as string} image={serviceDetailsData?.imageUrl}/>
            <DetailsContent<SERVICE_DETAILS_KEYS, IGetServiceDetailsResponse>
                title="Дані"
                onSetUpdatedData={handleUpdateServiceData}
                setDetailsData={setServiceDetailsData}
                detailsData={serviceDetailsData}
                detailsTitles={SERVICE_DETAILS_TITLES}
                lockedFields={getLockedFields(SERVICE_DETAILS_TITLES, canI)}
            />
            <Box className={s.actions}>
                <Button
                    startIcon={<Save opacity={!Object.values(serviceUpdatedData).length ? 0.3 : 1}/>}
                    variant="outlined"
                    disabled={!Object.values(serviceUpdatedData).length}
                    onClick={handleSubmitUpdateService}
                >
                    Зберегти
                </Button>
            </Box>
            {serviceDetailsRegData &&
                <DetailsContent<SERVICE_DETAILS_REG_KEYS, IRegDataObject>
                    title="Реквізити"
                    onSetUpdatedData={handleUpdateServiceData}
                    setDetailsData={setServiceDetailsRegData}
                    detailsData={serviceDetailsRegData}
                    detailsTitles={SERVICE_DETAILS_REG_TITLES}
                    lockedFields={SERVICES_LOCKED_REG_FIELDS}
                />
            }
            {serviceDetailFeeInnerData &&
                <Box className={s.fee}>
                    <DetailsContent<SERVICE_DETAILS_FEE_KEYS, IFeeObjectAllData>
                        title="Внутрішня комісія"
                        onSetUpdatedData={handleUpdateServiceFeeInnerData}
                        setDetailsData={setServiceDetailFeeInnerData}
                        detailsData={serviceDetailFeeInnerData}
                        detailsTitles={SERVICE_DETAILS_FEE_TITLES}
                        lockedFields={SERVICES_LOCKED_FEE_FIELDS}
                    />
                    <DetailsContent<SERVICE_DETAILS_FEE_KEYS, IFeeObjectAllData>
                        title="Вимоги партнера по min та max сумі платежу"
                        onSetUpdatedData={handleUpdateServiceFeeInnerData}
                        setDetailsData={setServiceDetailFeeInnerData}
                        detailsData={serviceDetailFeeInnerData}
                        detailsTitles={SERVICE_DETAILS_SUM_TITLES}
                        lockedFields={SERVICES_LOCKED_FEE_FIELDS}
                    />
                    <Box className={s.actions}>
                        <Button
                            startIcon={<Save opacity={serviceUpdatedFeeInnerData === null ? 0.3 : 1}/>}
                            variant="outlined"
                            disabled={serviceUpdatedFeeInnerData === null}
                            onClick={() => handleSubmitUpdateFeeService('INNER')}
                        >
                            Зберегти
                        </Button>
                    </Box>
                </Box>
            }
            {serviceDetailFeeOuterData &&
                <Box className={s.fee}>
                    <DetailsContent<SERVICE_DETAILS_FEE_KEYS, IFeeObjectAllData>
                        title="Зовнішня комісія (картки інших банків)"
                        onSetUpdatedData={handleUpdateServiceFeeOuterData}
                        setDetailsData={setServiceDetailFeeOuterData}
                        detailsData={serviceDetailFeeOuterData}
                        additionalData={serviceDetailFeeInnerData}
                        detailsTitles={SERVICE_DETAILS_FEE_TITLES}
                        lockedFields={getLockedFields(SERVICE_DETAILS_FEE_TITLES, canI)}
                        renderItemFooter={(item) =>
                            item.key === SERVICE_DETAILS_FEE_KEYS.FIXED_AMOUNT && isClientFeeForbidden ? (
                                <Box className={s.outerFeeForbiddenRow}>
                                    <Box className={s.outerFeeForbiddenDivider} />
                                    <Box className={s.outerFeeForbiddenItem}>
                                        <Typography variant="h6">Комісія з клієнта заборонена</Typography>
                                    </Box>
                                </Box>
                            ) : null
                        }
                    />
                    <DetailsContent<SERVICE_DETAILS_FEE_KEYS, IFeeObjectAllData>
                        title="Зовнішня мін. та макс. сума платежу"
                        onSetUpdatedData={handleUpdateServiceFeeOuterData}
                        setDetailsData={setServiceDetailFeeOuterData}
                        additionalData={serviceDetailFeeInnerData}
                        detailsData={serviceDetailFeeOuterData}
                        detailsTitles={SERVICE_DETAILS_SUM_TITLES}
                        lockedFields={getLockedFields(SERVICE_DETAILS_SUM_TITLES, canI)}
                    />
                    <Box className={s.actions}>
                        <Button
                            startIcon={<Save opacity={serviceUpdatedFeeOuterData === null ? 0.3 : 1}/>}
                            variant="outlined"
                            disabled={serviceUpdatedFeeOuterData === null}
                            onClick={() => handleSubmitUpdateFeeService('OUTER')}
                        >
                            Зберегти
                        </Button>
                    </Box>
                </Box>
            }
            {!!serviceDetailsPaymentMethods && (
				<Box className={s.fee}>
					<DetailsContent<SERVICE_DETAILS_PAYMENT_METHODS_KEYS, IPaymentMethodsSelectData>
						title="Дозволені методи оплати"
						onSetUpdatedData={handleUpdateServicePaymentMethods}
						setDetailsData={setServiceDetailsPaymentMethods}
						detailsData={serviceDetailsPaymentMethods}
						detailsTitles={SERVICE_DETAILS_PAYMENT_METHODS_TITLES}
						lockedFields={getLockedFields(SERVICE_DETAILS_PAYMENT_METHODS_TITLES, canI)}
					/>
					<Box className={s.actions}>
						<Button
							startIcon={<Save opacity={serviceUpdatedPaymentMethods === null ? 0.3 : 1} />}
							variant="outlined"
							disabled={serviceUpdatedPaymentMethods === null}
							onClick={handleSubmitUpdateServicePaymentMethods}
						>
							Зберегти
						</Button>
					</Box>
				</Box>
			)}
        </Box>
    );
};
